import { CSSProperties, ReactNode, useMemo } from 'react';
import {
  SxProps,
  Table as MUITable,
  TableContainer as MUITableContainer,
  TableContainerProps as MUITableContainerProps,
  TableProps as MUITableProps,
} from '@mui/material';

import TableBody from './TableBody';
import TableCell from './TableCell';
import TableFooter from './TableFooter';
import TableHead from './TableHead';
import TablePagination from './TablePagination';
import TableRow from './TableRow';

type AtomMUIContainerProps = {
  style?: CSSProperties;
};

type CustomProps = {
  /**
   * sets the top offset for the full page table
   */
  offsetTop?: string;
  /**
   * optional props to pass to the TableContainer that wraps ListTable
   */
  TableContainerProps?: AtomMUIContainerProps;
  /**
   * fullHeight causes table to utilize the height of calc(100vh - ${offsetTop}).
   * When this is false, the table's height will dynamically change based on
   * row heights. fullHeight will default as true.
   */
  fullHeight?: boolean;
};

type AtomMUITableProps = {
  stickyHeader?: boolean;
  children?: ReactNode;
};

export type ListTableProps = CustomProps & AtomMUITableProps;

export const ListTable = (props: ListTableProps) => {
  const {
    offsetTop,
    fullHeight = true,
    TableContainerProps = {},
    stickyHeader = true,
    ...data
  } = props;

  const sx: SxProps = useMemo(
    () => ({
      [fullHeight ? 'height' : 'maxHeight']: `calc(100vh - ${offsetTop})`,
    }),
    [fullHeight, offsetTop],
  );

  const MUIProps: MUITableProps = data;
  const MUIContainerProps: MUITableContainerProps = TableContainerProps;

  return (
    <MUITableContainer sx={sx} {...MUIContainerProps}>
      <MUITable stickyHeader={stickyHeader} {...MUIProps} />
    </MUITableContainer>
  );
};

ListTable.TableBody = TableBody;
ListTable.TableCell = TableCell;
ListTable.TableFooter = TableFooter;
ListTable.TableHead = TableHead;
ListTable.TablePagination = TablePagination;
ListTable.TableRow = TableRow;
